import { css } from '@emotion/react';
import { t } from 'i18n';
import { PropsWithChildren, useState } from 'react';
import type { Cabin, PassengerPrice, FareRules } from 'shared';
import { PAX_TYPE, formatCurrency, formatLongRules, formatPoints } from 'shared';

import { SasIcon, SasIconButton } from '../SasIcon';
import { SasModal } from '../SasModal';
import { theme } from '../theme';

import { getServiceClassColor } from './utils';

const styles = {
  productCardContainer: css`
    min-width: 100%;
    min-height: 340px;
    padding: ${theme.spacing.sm} ${theme.spacing.xs} ${theme.spacing.xss} ${theme.spacing.xs};

    @media (min-width: ${theme.breakpoints.mobileL}) {
      padding: ${theme.spacing.md} ${theme.spacing.xs} ${theme.spacing.xss} ${theme.spacing.xs};
    }
  `,
  productCardTitleWrapper: (color: string | undefined, backgroundColor: string | undefined) => css`
    min-height: 52px;
    padding: 0 ${theme.spacing.md};
    border-bottom: ${theme.borders.widths.sm} solid var(--color-gray-2);
    border-top-left-radius: ${theme.borders.radius.sm};
    border-top-right-radius: ${theme.borders.radius.sm};
    background-color: ${backgroundColor ?? 'var(--color-gray-1)'};
    color: ${color ?? 'var(--color-text)'};
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  productCardWrapper: (isSelected: boolean) => css`
    position: relative;
    padding-bottom: 53px;
    outline: 1px solid var(--color-gray-2);
    border-radius: ${theme.borders.radius.sm};
    background: var(--color-white);
    height: 100%;
    outline: ${isSelected ? '2px solid var(--color-brand)' : '1px solid var(--color-gray-2)'};
    cursor: pointer;
    container-type: inline-size;
  `,
  productCardFooter: css`
    position: absolute;
    bottom: 0;
    width: 100%;
    display: flex;
  `,
  productCardFooterContent: css`
    width: 100%;
    height: 52px;
    padding: ${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.md};
    text-align: left;
    display: flex;
  `,
  productCardFooterPrice: (hasDiscount: boolean) => css`
    font-size: ${hasDiscount ? theme.fontSize.sm : theme.fontSize.md};
    font-weight: ${hasDiscount ? theme.fontWeight.normal : theme.fontWeight.bold};
    text-decoration: ${hasDiscount ? 'line-through' : 'none'};
  `,
  productCardFooterPerPassenger: css`
    font-size: ${theme.fontSize.sm};
    font-weight: ${theme.fontWeight.normal};
  `,
  productCardFooterPPPwrapper: css`
    padding-left: ${theme.spacing.xs};
    display: flex;
    align-items: baseline;
    min-width: 150px;
  `,
  fontWeightBlack: css`
    font-weight: ${theme.fontWeight.black};
    padding-left: ${theme.spacing.sm};
    text-transform: uppercase;
  `,
  bonusHalfCircleWrapper: css`
    position: absolute;
    left: 0;
    top: 0;
    width: 300px;
    height: 51px;
    overflow: hidden;
    background: transparent;
    border-radius: ${theme.borders.radius.md};
  `,
  bonusfCircle: css`
    position: absolute;
    top: -120px;
    left: -15px;
    width: 235px;
    height: 205px;
    border-radius: 100%;
    background: ${theme.colors.background.ebGold};
    @container (max-width: 246px) {
      width: 220px;
    }
    @container (max-width: 227px) {
      width: 170px;
    }
  `,
  bonusSymbol: css`
    display: inline-block;
    width: 12px;
    height: 12px;
    background-color: ${theme.colors.background.ebBonusGold};
    border-radius: ${theme.borders.radius.lg};
  `,
  cardTitleBonusClass: css`
    display: inline-block;
    position: relative;
    font-size: ${theme.fontSize.sm};
  `,
  dicountedPoints: css`
    font-weight: ${theme.fontWeight.black};
    margin-right: ${theme.spacing.sm};
  `,
  infoButtonWrapper: css`
    z-index: 1;
  `,
  selectedLabel: css`
    font-size: ${theme.fontSize.sm};
    align-self: center;
    text-transform: capitalize;
  `,
};

const getPrice = (
  price: PassengerPrice | undefined,
  pointOfSale: string,
  revenuePrice: (price: PassengerPrice) => number
): string => {
  if (!price) {
    return 'NA';
  }

  return price.type === 'points'
    ? formatPoints(price.points, pointOfSale)
    : formatCurrency(revenuePrice(price), pointOfSale);
};

type Props = PropsWithChildren<{
  cabin: Cabin | Cabin;
  selectedProductId: string | undefined;
  pointOfSale: string;
  handleProductClick: (productId: string, shouldToggle?: boolean) => void;
  fareRules: Record<string, FareRules>;
  revenuePrice: (price: PassengerPrice) => number;
  isSarRebooking?: boolean;
}>;

export function FlightProductCard({
  cabin,
  selectedProductId,
  pointOfSale,
  handleProductClick,
  fareRules,
  children,
  revenuePrice,
  isSarRebooking = false,
}: Props) {
  const [showLongRulesModal, setShowLongRulesModal] = useState(false);

  const { color, backgroundColor, infoIconColor } = getServiceClassColor(cabin.productName);
  const pricePerPax = cabin.price.pricePerPassengerType.find(
    price => price.type === PAX_TYPE.ADULT || price.type === PAX_TYPE.YOUTH
  );

  const longRules = fareRules[cabin.productCode]?.longRules || [];

  const toggleLongRulesModal = (e: React.MouseEvent<Element>) => {
    e.stopPropagation();
    setShowLongRulesModal(showPopup => !showPopup);
  };

  const handleClick = () => handleProductClick(cabin.selectorId);

  const isSelected = selectedProductId === cabin.selectorId;
  const isStandardAward = cabin.isStandardAward;
  const hasDiscount = pricePerPax?.price?.type === 'points' && Boolean(pricePerPax?.price?.discountedPoints);

  return (
    <div css={styles.productCardContainer}>
      <div
        css={styles.productCardWrapper(isSelected)}
        tabIndex={1}
        onClick={handleClick}
        onKeyDown={e => e.key === 'Enter' && handleClick()}
      >
        <div css={styles.productCardTitleWrapper(color, backgroundColor)}>
          {isStandardAward && (
            <span css={styles.bonusHalfCircleWrapper}>
              <span css={styles.bonusfCircle}></span>
            </span>
          )}
          <div css={styles.cardTitleBonusClass}>
            {isStandardAward && <span css={styles.bonusSymbol}></span>}
            <span css={styles.fontWeightBlack}>{cabin.productName}</span>
          </div>
          <div css={styles.infoButtonWrapper}>
            <SasIconButton onClick={toggleLongRulesModal} name="cl--info-circle" color={infoIconColor} />
          </div>
        </div>

        {children}

        <footer css={styles.productCardFooter}>
          <div css={styles.productCardFooterContent}>
            <SasIcon
              name={isSelected ? 'in--radio-button-selected' : 'in--radio-button-unselected'}
              color={isSelected ? theme.colors.blue[4] : theme.colors.gray[2]}
            />

            <div css={styles.productCardFooterPPPwrapper}>
              {pricePerPax?.price?.type === 'points' ? (
                <>
                  {isSarRebooking ? (
                    <p css={styles.selectedLabel}>{t('sar.select')}</p>
                  ) : (
                    <>
                      {hasDiscount && (
                        <span css={styles.dicountedPoints}>
                          {formatPoints(pricePerPax.price.discountedPoints, pointOfSale)}
                        </span>
                      )}
                      <p css={styles.productCardFooterPrice(hasDiscount)}>
                        {getPrice(pricePerPax?.price, pointOfSale, revenuePrice)}
                      </p>
                    </>
                  )}
                </>
              ) : (
                <p css={styles.productCardFooterPrice(hasDiscount)}>
                  {getPrice(pricePerPax?.price, pointOfSale, revenuePrice)}
                </p>
              )}

              {!isSarRebooking && <p css={styles.productCardFooterPerPassenger}>/ {t('common:unit.adult.one')}</p>}
            </div>
          </div>
        </footer>
      </div>

      <SasModal open={showLongRulesModal} handleClose={() => setShowLongRulesModal(false)}>
        <>
          <h3 style={{ marginTop: '0px' }}>{cabin.productName}</h3>
          <p
            dangerouslySetInnerHTML={{
              __html: formatLongRules(longRules),
            }}
          />
          <p>
            <strong>{t('class', { class: Array.from(cabin.fareKey).join(', ') })}</strong>
          </p>
        </>
      </SasModal>
    </div>
  );
}
